import type { ComponentProps, ComponentType } from 'react';

import { Boxes, Braces, Certificate, ClipboardList, LayoutDashboard, ReceiptText, Settings, UserCog } from '@repo/media/icons';

import type { FileRoutesByTo } from '~/routeTree.gen.ts';
import type { FeatureGate } from '~/services/authorization/types.ts';
import { organization, project } from '~/services/authorization/types.ts';

export type Route<T = string> = { path: T; title: string; features: FeatureGate[]; icon?: ComponentType<ComponentProps<typeof Certificate>> };

export const routesByPath: { [Key in keyof FileRoutesByTo]: Route<Key> } = {
  '/': {
    path: '/',
    title: 'Northern Lights',
    features: [],
  },
  '/organization/$org': {
    path: '/organization/$org',
    title: 'Dashboard',
    features: [],
    icon: LayoutDashboard,
  },
  '/organization/$org/user-mgmt': {
    path: '/organization/$org/user-mgmt',
    title: 'User management',
    features: [organization('user-management')],
    icon: UserCog,
  },
  '/organization/$org/user-mgmt/users': {
    path: '/organization/$org/user-mgmt/users',
    title: 'Users',
    features: [organization('user-management')],
  },
  '/organization/$org/user-mgmt/service-accounts': {
    path: '/organization/$org/user-mgmt/service-accounts',
    title: 'Service accounts',
    features: [organization('user-management')],
  },
  '/organization/$org/user-mgmt/roles': {
    path: '/organization/$org/user-mgmt/roles',
    title: 'Roles',
    features: [organization('user-management')],
  },
  '/organization/$org/user-mgmt-guide': {
    path: '/organization/$org/user-mgmt-guide',
    title: 'Guide',
    features: [organization('user-management')],
  },
  '/organization/$org/api-docs': {
    path: '/organization/$org/api-docs',
    title: 'API documentation',
    features: [organization('api-documentation:read')],
    icon: Braces,
  },
  '/organization/$org/guides/$guide': {
    path: '/organization/$org/guides/$guide',
    title: 'Guides',
    features: [organization('api-documentation:read')],
  },
  '/project/$project': {
    path: '/project/$project',
    title: 'Dashboard',
    features: [],
    icon: LayoutDashboard,
  },
  '/project/$project/certificates': {
    path: '/project/$project/certificates',
    title: 'Certificates',
    features: [project('certificates')],
    icon: Certificate,
  },
  '/project/$project/certificates/$cert': {
    path: '/project/$project/certificates/$cert',
    title: 'Certificate',
    features: [project('certificates')],
  },
  '/project/$project/user-mgmt': {
    path: '/project/$project/user-mgmt',
    title: 'User management',
    features: [project('user-management')],
    icon: UserCog,
  },
  '/project/$project/user-mgmt/users': {
    path: '/project/$project/user-mgmt/users',
    title: 'Users',
    features: [project('user-management')],
  },
  '/project/$project/user-mgmt/service-accounts': {
    path: '/project/$project/user-mgmt/service-accounts',
    title: 'Service accounts',
    features: [project('user-management')],
  },
  '/project/$project/user-mgmt/roles': {
    path: '/project/$project/user-mgmt/roles',
    title: 'Roles',
    features: [project('user-management')],
  },
  '/project/$project/user-mgmt-guide': {
    path: '/project/$project/user-mgmt-guide',
    title: 'Guide',
    features: [project('user-management')],
  },
  '/project/$project/invoices': {
    path: '/project/$project/invoices',
    title: 'Invoices',
    icon: ReceiptText,
    features: [project('invoice')],
  },
  '/project/$project/invoices/$invoice': {
    path: '/project/$project/invoices/$invoice',
    title: 'Invoice',
    features: [project('invoice')],
  },
  '/project/$project/cargoes': {
    path: '/project/$project/cargoes',
    title: 'Cargoes',
    features: [project('cargos')],
    icon: Boxes,
  },
  '/project/$project/cargoes/$cargo': {
    path: '/project/$project/cargoes/$cargo',
    title: 'Cargo',
    features: [project('cargos')],
  },
  '/project/$project/declarations': {
    path: '/project/$project/declarations',
    title: 'Declarations',
    features: [project('composition')],
    icon: ClipboardList,
  },
  '/project/$project/declarations/$declaration': {
    path: '/project/$project/declarations/$declaration',
    title: 'Declaration',
    features: [project('composition')],
  },
  '/project/$project/composition/setup': {
    path: '/project/$project/composition/setup',
    title: 'Composition setup',
    features: [project('composition:manage-basis')],
    icon: Settings,
  },
};
